import { ContentSwitcher } from '@app/components/various/ContentSwitcher';
import { GridLarge, GridMedium, GridSmall, List } from '@app/components/various/Icons';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getIsProductsGridView, getProductsGridSize, setProductsGridSize, setProductsGridView, setProductsListView } from '@app/ducks';
import { useIsScreenSize } from '@app/utils/hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Switcher } from '../../../../../typings';

export const ProductsContentSwitcher = () => {
  const { t } = useTranslation(['common', 'confirmationConfig', 'products', 'orders']);
  const isProductsGridView = useSelector(getIsProductsGridView);
  const productsGridSize = useSelector(getProductsGridSize);
  const isSmallMobile = useIsScreenSize(BREAKPOINTS.XXS);
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);
  const dispatch = useDispatch();

  const handleSetGridView = React.useCallback(() => {
    dispatch(setProductsGridView());
  }, [dispatch]);

  const handleSetListView = React.useCallback(() => {
    dispatch(setProductsListView());
  }, [dispatch]);

  React.useEffect(() => {
    if (isSmallMobile && productsGridSize !== 'large') {
      dispatch(setProductsGridSize('large'));
    }
    if (isMobile && productsGridSize === 'small') {
      dispatch(setProductsGridSize('medium'));
    }
  }, [dispatch, isMobile, productsGridSize, isSmallMobile]);

  const viewOptions = {
    large: () => dispatch(setProductsGridSize('large')),
    medium: () => dispatch(setProductsGridSize('medium')),
    small: () => dispatch(setProductsGridSize('small')),
    viewAsGrid: () => handleSetGridView(),
    viewAsList: () => handleSetListView(),
  };
  const handleViewTypeChange = (type: keyof typeof viewOptions) => {
    viewOptions[type]();
    window.scrollTo({ top: 0 });
  };

  const gridOptionsTooltips = {
    large: t('common:large_grid_view'),
    medium: t('common:medium_grid_view'),
    small: t('common:small_grid_view'),
  };

  const gridOptionsIcons = {
    large: GridLarge,
    medium: GridMedium,
    small: GridSmall,
  };

  const tabs = [
    {
      dropdownTitle: t('common:grid_size'),
      icon: gridOptionsIcons[productsGridSize],
      options:
        isSmallMobile ?
          []
        : [
            {
              icon: GridLarge,
              key: 'large',
              label: t('common:large'),
            },
            {
              icon: GridMedium,
              key: 'medium',
              label: t('common:medium'),
            },
            ...(isMobile ?
              []
            : [
                {
                  icon: GridSmall,
                  key: 'small',
                  label: t('common:small'),
                },
              ]),
          ],
      title: gridOptionsTooltips[productsGridSize],
      value: 'viewAsGrid',
    },
    {
      icon: List,
      title: t('common:list_view'),
      value: 'viewAsList',
    },
  ] as Switcher.Tab<string>[];

  return (
    <ContentSwitcher
      groupName={t('common:product_view')}
      tabs={tabs}
      defaultNestedOptions={{
        viewAsGrid: productsGridSize,
      }}
      selectedValue={isProductsGridView ? 'viewAsGrid' : 'viewAsList'}
      onChange={handleViewTypeChange}
    />
  );
};
